<template>
    <Dialog :visible="visible" @update:visible="updateVisible" header="Download de arquivos em lote" class="w-11 lg:w-8"
        modal @show="onShow" @hide="onHide">
        <p class="text-sm text-color-secondary mb-3">
            {{ origem === 'contratos'
                ? `${selectedCount} contrato(s) selecionado(s). A seleção da grid prevalece; filtros opcionais refinam boletos/comprovantes vinculados.`
                : `${selectedCount} conta(s) selecionada(s). A seleção da grid prevalece sobre os filtros opcionais.` }}
        </p>

        <form @submit.prevent="gerarLote">
            <div class="grid">
                <div class="col-12">
                    <label class="block text-sm mb-2">Tipos de arquivo<span class="text-red-500">*</span></label>
                    <MultiSelect v-model="form.tipos_arquivo" :options="tipoOptions" optionLabel="label"
                        optionValue="value" display="chip" class="w-full" placeholder="Selecione os tipos" />
                    <div v-if="errors.tipos_arquivo" class="text-xs text-red-500 mt-1">{{ errors.tipos_arquivo }}</div>
                </div>

                <div v-if="origem === 'contratos'" class="col-12 md:col-6">
                    <FloatLabel class="w-full">
                        <Select id="lote_etapa" v-model="form.etapa" :options="etapaOptions" optionLabel="label"
                            optionValue="value" class="w-full" showClear />
                        <label for="lote_etapa">Etapa (filtro adicional)</label>
                    </FloatLabel>
                </div>

                <div v-if="origem === 'contratos'" class="col-12 md:col-6">
                    <FloatLabel class="w-full">
                        <Select id="lote_status_pagamento" v-model="form.status_pagamento"
                            :options="statusPagamentoOptions" optionLabel="label" optionValue="value" class="w-full"
                            showClear />
                        <label for="lote_status_pagamento">Status pagamento (contas do contrato)</label>
                    </FloatLabel>
                </div>

                <template v-if="origem === 'contratos'">
                    <div class="col-12 md:col-6">
                        <FloatLabel class="w-full">
                            <DatePicker id="lote_periodo_inicio" v-model="form.periodo_inicio" showIcon class="w-full"
                                dateFormat="dd/mm/yy" />
                            <label for="lote_periodo_inicio">Vencimento contas — início</label>
                        </FloatLabel>
                    </div>

                    <div class="col-12 md:col-6">
                        <FloatLabel class="w-full">
                            <DatePicker id="lote_periodo_fim" v-model="form.periodo_fim" showIcon class="w-full"
                                dateFormat="dd/mm/yy" />
                            <label for="lote_periodo_fim">Vencimento contas — fim</label>
                        </FloatLabel>
                    </div>

                    <div class="col-12 md:col-6">
                        <FloatLabel class="w-full">
                            <AutoComplete id="lote_cliente" v-model="selectedCliente" :suggestions="clienteSuggestions"
                                optionLabel="nome" class="w-full" forceSelection @complete="searchCliente"
                                @item-select="onSelectCliente" @clear="onClearCliente" dropdown />
                            <label for="lote_cliente">Cliente (filtro adicional)</label>
                        </FloatLabel>
                    </div>
                </template>
            </div>

            <div class="flex justify-content-end gap-2 mt-4">
                <Button type="button" label="Limpar filtros" severity="secondary" size="small" @click="limparFiltros" />
                <Button type="submit" label="Gerar ZIP" icon="pi pi-download" severity="success" size="small"
                    :loading="isSubmitting" />
            </div>
        </form>

        <Divider />

        <div class="flex justify-content-between align-items-center mb-2">
            <span class="font-semibold">Meus lotes recentes</span>
            <div class="flex gap-1">
                <Button icon="pi pi-trash" label="Limpar erros" severity="danger" size="small" text
                    :disabled="!temLotesErro" :loading="isClearing" @click="limparErros" />
                <Button icon="pi pi-eraser" label="Limpar histórico" severity="secondary" size="small" text
                    :disabled="!lotes.length" :loading="isClearing" @click="limparHistorico" />
                <Button icon="pi pi-refresh" label="Atualizar" size="small" text @click="loadLotes"
                    :loading="isLoadingLotes" />
            </div>
        </div>

        <DataTable :value="lotes" :loading="isLoadingLotes" size="small" stripedRows showGridlines>
            <Column field="id" header="#" style="width: 70px" />
            <Column field="created_at" header="Solicitado em">
                <template #body="{ data }">
                    {{ dateFormat(data.created_at) }}
                </template>
            </Column>
            <Column header="Progresso" style="min-width: 180px">
                <template #body="{ data }">
                    <ProgressBar :value="data.progresso" :showValue="true" style="height: 1rem" />
                    <div class="text-xs text-color-secondary mt-1">
                        {{ data.processados }}/{{ data.total_arquivos || '—' }} arquivos
                    </div>
                </template>
            </Column>
            <Column field="status_label" header="Status">
                <template #body="{ data }">
                    <Tag :severity="statusSeverity(data.status)" :value="statusLabel(data.status)" />
                    <div v-if="data.status === STATUS.ERRO && data.erros" class="text-xs text-red-500 mt-1">
                        {{ data.erros }}
                    </div>
                    <div v-if="data.avisos" class="text-xs text-color-secondary mt-1 whitespace-pre-line">
                        {{ data.avisos }}
                    </div>
                </template>
            </Column>
            <Column header="Ações" style="width: 150px">
                <template #body="{ data }">
                    <div class="flex gap-1">
                        <Button v-if="data.status === STATUS.PRONTO && data.arquivo_id" icon="pi pi-download"
                            severity="success" size="small" rounded text v-tooltip.top="'Baixar ZIP'"
                            :loading="downloadingId === data.id" @click="downloadLote(data)" />
                        <Button v-if="data.status !== STATUS.PROCESSANDO" icon="pi pi-trash" severity="danger"
                            size="small" rounded text v-tooltip.top="'Excluir lote'"
                            :loading="removingId === data.id" @click="removerLote(data)" />
                    </div>
                </template>
            </Column>
            <template #empty>
                <div class="text-center text-color-secondary py-3">Nenhum lote encontrado.</div>
            </template>
        </DataTable>
    </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import MultiSelect from 'primevue/multiselect';
import Select from 'primevue/select';
import FloatLabel from 'primevue/floatlabel';
import DatePicker from 'primevue/datepicker';
import AutoComplete from 'primevue/autocomplete';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import ProgressBar from 'primevue/progressbar';
import Tag from 'primevue/tag';
import Divider from 'primevue/divider';

import ArquivoLote from '@/service/arquivoLote.js';
import Cliente from '@/service/cliente.js';
import Etapa from '@/service/etapa.js';
import File from '@/utils/File.js';
import Moment from 'moment';

const STATUS = {
    PENDENTE: 0,
    PROCESSANDO: 1,
    PRONTO: 2,
    ERRO: 3,
};

const NOTIFIED_STORAGE_KEY = 'gts_arquivo_lote_notified';

export default {
    name: 'ArquivoLoteDialog',
    components: {
        Dialog,
        Button,
        MultiSelect,
        Select,
        FloatLabel,
        DatePicker,
        AutoComplete,
        DataTable,
        Column,
        ProgressBar,
        Tag,
        Divider,
    },
    props: {
        visible: { type: Boolean, default: false },
        origem: { type: String, default: 'contratos' },
        selectedIds: { type: Array, default: () => [] },
    },
    emits: ['close'],
    data() {
        return {
            STATUS,
            form: this.defaultForm(),
            errors: {},
            tipoOptions: [],
            etapaOptions: [],
            statusPagamentoOptions: [
                { label: 'Pago', value: 'pago' },
                { label: 'Pendente', value: 'pendente' },
            ],
            selectedCliente: null,
            clienteSuggestions: [],
            lotes: [],
            isSubmitting: false,
            isLoadingLotes: false,
            downloadingId: null,
            removingId: null,
            isClearing: false,
            pollTimer: null,
            notifiedIds: [],
            silentFirstLoad: true,
        };
    },
    computed: {
        selectedCount() {
            return this.selectedIds.length;
        },
        temLotesErro() {
            return this.lotes.some(lote => lote.status === STATUS.ERRO);
        },
    },
    beforeUnmount() {
        this.stopPolling();
    },
    methods: {
        defaultForm() {
            return {
                tipos_arquivo: [],
                etapa: null,
                status_pagamento: null,
                periodo_inicio: null,
                periodo_fim: null,
                cliente_id: null,
            };
        },
        updateVisible(visible) {
            if (!visible) {
                this.$emit('close');
            }
        },
        onShow() {
            this.errors = {};
            this.notifiedIds = this.loadNotifiedIds();
            this.silentFirstLoad = true;
            this.loadTipos();
            this.loadEtapas();
            this.loadLotes();
            this.startPolling();
        },
        onHide() {
            this.stopPolling();
            this.form = this.defaultForm();
            this.selectedCliente = null;
            this.saveNotifiedIds();
        },
        loadNotifiedIds() {
            try {
                const raw = sessionStorage.getItem(NOTIFIED_STORAGE_KEY);
                return raw ? JSON.parse(raw) : [];
            } catch (e) {
                return [];
            }
        },
        saveNotifiedIds() {
            sessionStorage.setItem(NOTIFIED_STORAGE_KEY, JSON.stringify(this.notifiedIds));
        },
        marcarComoNotificado(lote) {
            if (lote.status === STATUS.PRONTO && !this.notifiedIds.includes(lote.id)) {
                this.notifiedIds.push(lote.id);
            }
            this.saveNotifiedIds();
        },
        limparFiltros() {
            this.form = this.defaultForm();
            this.selectedCliente = null;
            this.loadTipos();
        },
        loadTipos() {
            ArquivoLote.tipos(this.origem)
                .then(response => {
                    this.tipoOptions = response.data.data || [];
                    if (this.form.tipos_arquivo.length === 0) {
                        this.form.tipos_arquivo = this.tipoOptions.map(item => item.value);
                    }
                });
        },
        loadEtapas() {
            if (this.origem !== 'contratos') {
                return;
            }
            Etapa.option('order[descricao]=asc')
                .then(response => {
                    this.etapaOptions = (response.data || []).map(item => ({
                        label: item.descricao,
                        value: item.id,
                    }));
                });
        },
        searchCliente(event) {
            Cliente.search(event.query)
                .then(response => {
                    this.clienteSuggestions = response.data;
                });
        },
        onSelectCliente(event) {
            this.form.cliente_id = event.value?.id || null;
        },
        onClearCliente() {
            this.selectedCliente = null;
            this.form.cliente_id = null;
        },
        buildPayload() {
            const payload = {
                origem: this.origem,
                tipos_arquivo: this.form.tipos_arquivo,
            };

            if (this.origem === 'contratos') {
                payload.contrato_ids = this.selectedIds;
                if (this.form.etapa) {
                    payload.etapa = this.form.etapa;
                }
            } else {
                payload.conta_ids = this.selectedIds;
            }

            if (this.form.cliente_id) {
                payload.cliente_id = this.form.cliente_id;
            }
            if (this.form.status_pagamento) {
                payload.status_pagamento = this.form.status_pagamento;
            }
            if (this.form.periodo_inicio) {
                payload.periodo_inicio = Moment(this.form.periodo_inicio).format('YYYY-MM-DD');
            }
            if (this.form.periodo_fim) {
                payload.periodo_fim = Moment(this.form.periodo_fim).format('YYYY-MM-DD');
            }

            return payload;
        },
        gerarLote() {
            this.errors = {};

            if (!this.selectedIds.length) {
                this.$toast.add({
                    severity: 'warn',
                    summary: 'Atenção',
                    detail: 'Selecione ao menos um registro na listagem.',
                    life: 5000,
                });
                return;
            }

            if (!this.form.tipos_arquivo.length) {
                this.errors.tipos_arquivo = 'Selecione ao menos um tipo de arquivo.';
                return;
            }

            this.isSubmitting = true;

            ArquivoLote.store(this.buildPayload())
                .then(response => {
                    const lote = response.data.data;
                    const severity = response.data.status === 'error' ? 'error' : 'success';
                    const summary = lote?.status === STATUS.PRONTO
                        ? 'ZIP pronto'
                        : (response.data.status === 'error' ? 'Erro no lote' : 'Lote enfileirado');

                    this.$toast.add({
                        severity,
                        summary,
                        detail: response.data.message || 'Operação concluída.',
                        life: 8000,
                    });

                    if (lote?.status === STATUS.PRONTO && lote.id) {
                        this.marcarComoNotificado(lote);
                    }

                    this.loadLotes();
                })
                .catch(error => {
                    const erros = error.response?.data?.erros;
                    if (erros) {
                        Object.keys(erros).forEach(campo => {
                            this.errors[campo] = Array.isArray(erros[campo]) ? erros[campo][0] : erros[campo];
                        });
                    }
                    const msg = error.response?.data?.msg || error.response?.data?.message || error.message;
                    this.$toast.add({ severity: 'error', summary: 'Ops!', detail: msg, life: 6000 });
                })
                .finally(() => {
                    this.isSubmitting = false;
                });
        },
        loadLotes() {
            this.isLoadingLotes = true;
            ArquivoLote.list('page=1&order[id]=desc')
                .then(response => {
                    this.lotes = response.data.data || [];

                    if (this.silentFirstLoad) {
                        this.lotes.forEach(lote => this.marcarComoNotificado(lote));
                        this.silentFirstLoad = false;
                    } else {
                        this.checkNotifications();
                    }
                })
                .finally(() => {
                    this.isLoadingLotes = false;
                });
        },
        checkNotifications() {
            this.lotes.forEach(lote => {
                if (lote.status === STATUS.PRONTO && !this.notifiedIds.includes(lote.id)) {
                    this.marcarComoNotificado(lote);
                    this.$toast.add({
                        severity: 'info',
                        summary: 'ZIP pronto',
                        detail: `Lote #${lote.id} disponível para download.`,
                        life: 6000,
                    });
                }
            });
        },
        startPolling() {
            this.stopPolling();
            this.pollTimer = setInterval(() => {
                const pendente = this.lotes.some(item =>
                    item.status === STATUS.PENDENTE || item.status === STATUS.PROCESSANDO
                );
                if (pendente) {
                    this.loadLotes();
                }
            }, 3000);
        },
        stopPolling() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer);
                this.pollTimer = null;
            }
        },
        downloadLote(lote) {
            this.downloadingId = lote.id;
            ArquivoLote.download(lote.id)
                .then(response => {
                    File.downloadBlob(response);
                    this.lotes = this.lotes.filter(item => item.id !== lote.id);
                })
                .catch(error => {
                    const msg = error.response?.data?.msg || error.message;
                    this.$toast.add({ severity: 'error', summary: 'Ops!', detail: msg, life: 6000 });
                })
                .finally(() => {
                    this.downloadingId = null;
                });
        },
        removerLote(lote) {
            this.$confirm.require({
                message: `Remover o lote #${lote.id} do histórico?`,
                header: 'Excluir lote',
                icon: 'pi pi-trash',
                acceptLabel: 'Excluir',
                rejectLabel: 'Cancelar',
                acceptClass: 'bg-red-500 text-white border-red-500',
                accept: () => {
                    this.removingId = lote.id;
                    ArquivoLote.destroy(lote.id)
                        .then(() => {
                            this.lotes = this.lotes.filter(item => item.id !== lote.id);
                            this.$toast.add({
                                severity: 'success',
                                summary: 'Removido',
                                detail: `Lote #${lote.id} excluído.`,
                                life: 4000,
                            });
                        })
                        .catch(error => {
                            const msg = error.response?.data?.msg || error.response?.data?.message || error.message;
                            this.$toast.add({ severity: 'error', summary: 'Ops!', detail: msg, life: 6000 });
                        })
                        .finally(() => {
                            this.removingId = null;
                        });
                },
            });
        },
        limparErros() {
            this.$confirm.require({
                message: 'Remover todos os lotes com erro do histórico?',
                header: 'Limpar erros',
                icon: 'pi pi-trash',
                acceptLabel: 'Limpar',
                rejectLabel: 'Cancelar',
                acceptClass: 'bg-red-500 text-white border-red-500',
                accept: () => this.executarLimpar(true),
            });
        },
        limparHistorico() {
            this.$confirm.require({
                message: 'Remover todo o histórico de lotes (exceto os em processamento)?',
                header: 'Limpar histórico',
                icon: 'pi pi-eraser',
                acceptLabel: 'Limpar tudo',
                rejectLabel: 'Cancelar',
                acceptClass: 'bg-red-500 text-white border-red-500',
                accept: () => this.executarLimpar(false),
            });
        },
        executarLimpar(apenasErro) {
            this.isClearing = true;
            ArquivoLote.limpar(apenasErro)
                .then(response => {
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Histórico',
                        detail: response.data.message,
                        life: 5000,
                    });
                    this.loadLotes();
                })
                .catch(error => {
                    const msg = error.response?.data?.msg || error.response?.data?.message || error.message;
                    this.$toast.add({ severity: 'error', summary: 'Ops!', detail: msg, life: 6000 });
                })
                .finally(() => {
                    this.isClearing = false;
                });
        },
        dateFormat(value) {
            if (!value) {
                return '—';
            }
            return Moment.utc(value).local().format('DD/MM/YYYY HH:mm');
        },
        statusLabel(status) {
            const map = {
                [STATUS.PENDENTE]: 'Pendente',
                [STATUS.PROCESSANDO]: 'Processando',
                [STATUS.PRONTO]: 'Pronto',
                [STATUS.ERRO]: 'Erro',
            };
            return map[status] || '—';
        },
        statusSeverity(status) {
            if (status === STATUS.PRONTO) {
                return 'success';
            }
            if (status === STATUS.ERRO) {
                return 'danger';
            }
            if (status === STATUS.PROCESSANDO) {
                return 'warn';
            }
            return 'secondary';
        },
    },
};
</script>
